import { useState } from 'react'
import { isMasked, sensitiveApi, type SensitiveEntityType } from '../api/sensitive'
import { useFeatureFlag } from '../lib/featureFlags'
import { Button, Dialog, FormGrid, TextField } from './ui'
import './shared.css'

const MIN_REASON = 5

/**
 * C-08: "Mostra dati completi". Compare solo con `privacy.mascheramento` acceso e se almeno uno dei valori indicati
 * arriva mascherato (o, senza `values`, sempre: per le schede i cui campi sensibili stanno in un componente figlio).
 * Chiede il motivo, che finisce nel registro di audit; la vista intera vale 15 minuti per questa scheda. Dopo la
 * conferma chiama `onRevealed` perche' la pagina rilegga i dati.
 */
export function RevealSensitiveButton({
  entityType,
  entityId,
  values,
  onRevealed,
}: {
  entityType: SensitiveEntityType
  entityId: number
  values?: Array<string | null | undefined>
  onRevealed: () => void
}) {
  const on = useFeatureFlag('privacy.mascheramento')
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [until, setUntil] = useState('')

  if (!on) return null
  if (values && !values.some(isMasked) && !until) return null

  const trimmed = reason.trim()

  async function confirm() {
    setBusy(true)
    setError('')
    try {
      const res = await sensitiveApi.reveal(entityType, entityId, trimmed)
      setUntil(res.expiresAt
        ? new Date(res.expiresAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        : '')
      setOpen(false)
      setReason('')
      onRevealed()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Richiesta non riuscita')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ecrm-inline-actions d-print-none">
      {until ? (
        <span className="ecrm-inline-message" role="status">Dati completi visibili fino alle {until}</span>
      ) : (
        <Button variant="outlined" size="sm" icon="visibility" onClick={() => setOpen(true)}>
          Mostra dati completi
        </Button>
      )}
      <Dialog
        open={open}
        onClose={() => { if (!busy) setOpen(false) }}
        title="Mostra dati completi"
        icon="visibility"
        supportingText="IBAN, codice fiscale e telefono si vedono interi per 15 minuti, solo su questa scheda. Il motivo resta nel registro di audit."
        as="form"
        dismissible={!busy}
        onSubmit={e => {
          e.preventDefault()
          if (trimmed.length >= MIN_REASON) void confirm()
        }}
        actions={
          <>
            <Button variant="text" disabled={busy} onClick={() => setOpen(false)}>Annulla</Button>
            <Button type="submit" variant="text" disabled={busy || trimmed.length < MIN_REASON}>Conferma</Button>
          </>
        }
      >
        <FormGrid columns={1}>
          <TextField
            multiline
            rows={3}
            label="Motivo"
            maxLength={500}
            value={reason}
            onChange={e => setReason(e.target.value)}
            supportingText={error || `Almeno ${MIN_REASON} caratteri`}
          />
        </FormGrid>
      </Dialog>
    </div>
  )
}
